import React, { createContext, useContext, useState, ReactNode } from 'react'
import { ThemeColors, BRAND_THEMES } from '../components/ThemeToggle'

interface ThemeContextType {
    currentTheme: string
    themeColors: ThemeColors
    customBackgroundColor: string | null
    customHeaderColor: string | null
    customCardColor: string | null
    customFontColor: string | null
    setTheme: (themeName: string) => void
    setCustomBackgroundColor: (color: string) => void
    setCustomHeaderColor: (color: string) => void
    setCustomCardColor: (color: string) => void
    setCustomFontColor: (color: string) => void
    getCardStyles: () => any
    getFontStyles: () => any
    getButtonStyles: () => any
    getBackgroundStyles: () => any
    getHeaderStyles: () => any
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

interface ThemeProviderProps {
    children: ReactNode
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
    const [currentTheme, setCurrentTheme] = useState<string>('brand-red')
    const [customBackgroundColor, setCustomBackgroundColor] = useState<string | null>(null)
    const [customHeaderColor, setCustomHeaderColor] = useState<string | null>(null)
    const [customCardColor, setCustomCardColor] = useState<string | null>(null)
    const [customFontColor, setCustomFontColor] = useState<string | null>(null)

    const themeColors = BRAND_THEMES[currentTheme]

    const setTheme = (themeName: string) => {
        setCurrentTheme(themeName)
        // Reset custom colors when switching to a preset theme
        setCustomBackgroundColor(null)
        setCustomHeaderColor(null)
        setCustomCardColor(null)
        setCustomFontColor(null)
    }

    const getCardStyles = () => ({
        backgroundColor: customCardColor || themeColors.cardBackground,
        color: customFontColor || themeColors.cardText,
        borderColor: customFontColor || themeColors.cardText === 'white' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)'
    })

    const getFontStyles = () => ({
        color: customFontColor || themeColors.cardText
    })

    const getButtonStyles = () => ({
        backgroundColor: customCardColor || themeColors.buttonBackground,
        color: customFontColor || themeColors.buttonText
    })

    const getBackgroundStyles = () => ({
        backgroundColor: customBackgroundColor || themeColors.background
    })

    const getHeaderStyles = () => ({
        backgroundColor: customHeaderColor || themeColors.headerBackground,
        color: customFontColor || themeColors.headerText
    })

    return (
        <ThemeContext.Provider
            value={{
                currentTheme,
                themeColors,
                customBackgroundColor,
                customHeaderColor,
                customCardColor,
                customFontColor,
                setTheme,
                setCustomBackgroundColor,
                setCustomHeaderColor,
                setCustomCardColor,
                setCustomFontColor,
                getCardStyles,
                getFontStyles,
                getButtonStyles,
                getBackgroundStyles,
                getHeaderStyles
            }}
        >
            {children}
        </ThemeContext.Provider>
    )
}

export const useTheme = (): ThemeContextType => {
    const context = useContext(ThemeContext)
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider')
    }
    return context
}